import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import LoginForm from "../components/LoginForm";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, onSnapshot, getDoc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import "../styles/home.css";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { usePageTitle } from "../hooks/usePageTitle";
import { useDemoContext } from "../context/DemoContext";

const parseFrenchDateString = (dateString: string): Date | null => {
  const monthMap: { [key: string]: number } = {
    'janv.': 0, 'févr.': 1, 'mars': 2, 'avr.': 3, 'mai': 4, 'juin': 5,
    'juil.': 6, 'août': 7, 'sept.': 8, 'oct.': 9, 'nov.': 10, 'déc.': 11
  };

  const parts = dateString.split(' ');
  if (parts.length < 5) {
    return null;
  }

  const day = parseInt(parts[0]);
  const monthName = parts[1].toLowerCase();
  const year = parseInt(parts[2]);
  const timeParts = parts[4].split(':');
  const hours = parseInt(timeParts[0]);
  const minutes = parseInt(timeParts[1]);
  const seconds = parseInt(timeParts[2]);

  const monthIndex = monthMap[monthName];
  if (monthIndex === undefined) {
    return null;
  }

  return new Date(year, monthIndex, day, hours, minutes, seconds);
};

// Helper function to convert any date-like value (Timestamp or string) to a Date object
const convertToDate = (value: any): Date | null => {
  if (!value) return null;

  if (typeof value.toDate === 'function') { // It's a Firebase Timestamp
    return value.toDate();
  } else if (typeof value === 'string') {
    // Try parsing as YYYY-MM-DD first (common for Firebase 'date' fields)
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date;
    }
    // Fallback to French date string parsing if YYYY-MM-DD fails
    return parseFrenchDateString(value);
  }
  return null; // Unexpected type
};

interface Opportunity {
  date: string;
  type: string;
  amount?: number;
  qte?: number;
  pu?: number;
  ttc?: number;
  description?: string;
  contact?: string;
  createdAt?: any;
}

interface DevisItem {
  amount?: number;
  contact?: string;
  date: string;
  updatedAt?: any;
  ttc?: number;
  status?: string;
  isSigned?: boolean;
}

interface CompaniesData {
  [companyName: string]: {
    opportunities?: Opportunity[];
    type?: string;
    devis?: DevisItem[];
    createdAt?: any;
    [key: string]: any;
  };
}

interface User {
  role?: string;
  email: string;
  createdAt: Date;
}

const Home = () => {
  usePageTitle("Accueil");
  const { companiesDocId } = useDemoContext();
  const [showLogin, setShowLogin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [companiesData, setCompaniesData] = useState<CompaniesData>({});
  const [foodinnovCompaniesData, setFoodinnovCompaniesData] = useState<CompaniesData>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const navigate = useNavigate();

  // Palette de couleurs professionnelle
  const COLORS = [
    '#2E86C1', // Bleu professionnel
    '#27AE60', // Vert émeraude
    '#E67E22', // Orange
    '#8E44AD', // Violet
    '#C0392B', // Rouge bordeaux
    '#16A085', // Turquoise
    '#D35400', // Orange foncé
    '#2980B9', // Bleu ciel
    '#2C3E50', // Bleu marine
    '#E74C3C', // Rouge
    '#3498DB', // Bleu clair
    '#1ABC9C'  // Vert menthe
  ];

  // Fonction pour formater les labels des camemberts (pourcentage centré uniquement)
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent === 0) return null;

    return (
      <text
        x={x}
        y={y}
        fill="#222"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={14}
        fontWeight={700}
        stroke="#fff"
        strokeWidth={2}
        paintOrder="stroke"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Label externe pour le camembert avec pourcentage devant le mot-clé
  const renderExternalLabel = ({ cx, cy, midAngle, outerRadius, percent, name }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 18;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill="#222"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
        stroke="#fff"
        strokeWidth={2}
        paintOrder="stroke"
        style={{ pointerEvents: 'none' }}
      >
        {`${(percent * 100).toFixed(0)}% ${name}`}
      </text>
    );
  };

  useEffect(() => {
    setLoading(true);
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsAuthenticated(!!user);
      if (user) {
        localStorage.setItem("isAuthenticated", "true");
        if (user.email) {
          try {
            const userDoc = await getDoc(doc(db, "Users", user.email!));
            if (userDoc.exists()) {
              const userInfo = userDoc.data() as User;
              setUserRole(userInfo.role || null);
            } else {
              setUserRole(null);
            }
          } catch (error) {
            console.error("Error fetching user role:", error);
            toast.error("Erreur lors de la récupération du rôle utilisateur.");
            setUserRole(null);
          }
        } else {
          setUserRole(null);
        }
      } else {
        localStorage.removeItem("isAuthenticated");
        setUserRole(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true);
      // Écouter les changements dans la collection companies
      const companiesRef = doc(db, "prospects", companiesDocId);
      const unsubscribeCompanies = onSnapshot(companiesRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as CompaniesData;
          setCompaniesData(data);
        }
      }, (error) => {
        toast.error("Erreur lors du chargement des sociétés.");
        console.error(error);
      });

      // Écouter les changements dans la collection foodinnovCompanies
      const foodinnovRef = doc(db, "prospects", "foodinnovCompanies");
      const unsubscribeFoodinnov = onSnapshot(foodinnovRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as CompaniesData;
          setFoodinnovCompaniesData(data);
        }
        setLoading(false);
      }, (error) => {
        toast.error("Erreur lors du chargement des sociétés FoodInnov.");
        setLoading(false);
        console.error(error);
      });

      return () => {
        unsubscribeCompanies();
        unsubscribeFoodinnov();
      };
    }
  }, [isAuthenticated, companiesDocId]);

  const handleLoginClick = () => {
    setShowLogin(true);
  };

  const createUserInFirestore = async (email: string, role: string = 'foodinnov') => {
    try {
      const userRef = doc(db, "Users", email);
      const userData: User = {
        email,
        role,
        createdAt: new Date()
      };
      await setDoc(userRef, userData);
    } catch (error) {
      console.error("Erreur lors de la création de l'utilisateur:", error);
    }
  };

  const handleLoginSuccess = async (user: any) => {
    setActionLoading(true);
    setIsAuthenticated(true);
    setShowLogin(false);

    if (!user.email) {
      console.error("Email de l'utilisateur non disponible après la connexion.");
      setUserRole(null);
      setActionLoading(false);
      return;
    }

    try {
      // Vérifier si l'utilisateur existe déjà dans Firestore
      const userDocRef = doc(db, "Users", user.email!);
      const userDocSnapshot = await getDoc(userDocRef);

      if (!userDocSnapshot.exists()) {
        const usersCountRef = doc(db, "Users", "count");
        const usersCountSnapshot = await getDoc(usersCountRef);
        const isFirstUser = !usersCountSnapshot.exists() || usersCountSnapshot.data()?.count === 0;
        
        const roleToAssign = isFirstUser ? 'admin' : 'foodinnov';
        await setDoc(userDocRef, {
          email: user.email,
          role: roleToAssign,
          createdAt: new Date()
        });

        // Mettre à jour le compteur d'utilisateurs
        await setDoc(usersCountRef, { count: (usersCountSnapshot.data()?.count || 0) + 1 });
      }
      // Après la connexion réussie, re-récupérer le rôle pour s'assurer qu'il est à jour
      try {
        const updatedUserDoc = await getDoc(doc(db, "Users", user.email!));
        if (updatedUserDoc.exists()) {
          const updatedUserInfo = updatedUserDoc.data() as User;
          setUserRole(updatedUserInfo.role || null);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération du rôle utilisateur mis à jour :", error);
        toast.error("Erreur lors de la récupération du rôle utilisateur mis à jour.");
      }
    } catch (error) {
      toast.error("Erreur lors de la connexion.");
      console.error(error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogoutClick = async () => {
    setActionLoading(true);
    try {
      await auth.signOut();
      setIsAuthenticated(false);
      setUserRole(null);
      setShowLogin(false);
    } catch (error) {
      toast.error("Erreur lors de la déconnexion.");
      console.error(error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  // Fonction pour extraire toutes les années disponibles à partir des factures
  const getAvailableYears = (): number[] => {
    const yearsSet = new Set<number>();
    
    // Toujours inclure l'année en cours
    const currentYear = new Date().getFullYear();
    yearsSet.add(currentYear);
    
    // Parcourir les factures de companiesData
    Object.values(companiesData).forEach(company => {
      company.invoices?.forEach((invoice: any) => {
        const invoiceDate = convertToDate(invoice.date);
        if (invoiceDate) {
          yearsSet.add(invoiceDate.getFullYear());
        }
      });
    });
    
    // Parcourir les factures de foodinnovCompaniesData
    Object.values(foodinnovCompaniesData).forEach(company => {
      company.invoices?.forEach((invoice: any) => {
        const invoiceDate = convertToDate(invoice.date);
        if (invoiceDate) {
          yearsSet.add(invoiceDate.getFullYear());
        }
      });
    });
    
    // Trier les années par ordre décroissant
    return Array.from(yearsSet).sort((a, b) => b - a);
  };

  // Montant facturé HT d'une facture (repli sur montantHT si le champ facturé est absent — cas des factures Foodinnov)
  const invoiceFactureHT = (inv: any) => Number(inv.montantFactureHT ?? inv.montantHT) || 0;

  const getCurrentMonthStats = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = selectedYear;

    const processData = (data: CompaniesData) => {
      let companies = 0;
      let quotes = 0;
      let ttcMonthly = 0;
      const requestTypes = new Map<string, number>();
      let totalCompaniesYearly = 0;
      let totalQuotesYearly = 0;
      let signedQuotesYearly = 0;
      let totalRequestsYear = 0;
      let totalRequestsMonth = 0;
      let totalDevisAmountYear = 0;
      let totalDevisCountYear = 0;
      let totalDevisCountMonth = 0;
      let totalDevisSignedYear = 0;
      let totalDevisAmountMonth = 0;
      const caMensuel: number[] = Array(12).fill(0);       // CA facturé (hors gelées)
      const caMensuelRegle: number[] = Array(12).fill(0);  // CA réglé uniquement
      const caMensuelGele: number[] = Array(12).fill(0);   // CA gelé (⚠️ non compté dans facturé/réglé)

      Object.values(data).forEach(company => {
        const createdAt = convertToDate(company.createdAt);
        if (createdAt && createdAt.getFullYear() === currentYear) {
            totalCompaniesYearly++;
            if (createdAt.getMonth() === currentMonth) {
                companies++;
            }
        }
        

        company.opportunities?.forEach(opp => {
          const oppDate = convertToDate(opp.createdAt || opp.date);
          if (oppDate && oppDate.getFullYear() === currentYear) {
            totalRequestsYear++;
            if (oppDate.getMonth() === currentMonth) {
              totalRequestsMonth++;
            }
            if (opp.type) {
              requestTypes.set(opp.type, (requestTypes.get(opp.type) || 0) + 1);
            }
          }
        });

        company.devis?.forEach(devis => {
          const devisDate = convertToDate(devis.date);
          if (devisDate && devisDate.getFullYear() === currentYear) {
            totalDevisCountYear++;
            if (typeof devis.amount === 'number') {
              totalDevisAmountYear += devis.amount;
              if (devisDate.getMonth() === currentMonth) {
                totalDevisAmountMonth += devis.amount;
              }
            }
            if (devis.isSigned) {
              totalDevisSignedYear++;
            }
            if (devisDate.getMonth() === currentMonth) {
              totalDevisCountMonth++;
            }
          }
        });

        company.invoices?.forEach((invoice: any) => {
          const invoiceDate = convertToDate(invoice.date);
          if (invoiceDate && invoiceDate.getFullYear() === currentYear) {
            const month = invoiceDate.getMonth();
            const status = invoice.status || 'En attente de Paiement';
            const montant = invoiceFactureHT(invoice);
            caMensuel[month] += montant;               // CA facturé = tout ce qui est facturé (En attente + Réglée + Gelée)
            if (status === 'Gelée') {
              caMensuelGele[month] += montant;         // dont ⚠️ gelé (non payé)
            } else if (status === 'Réglée') {
              caMensuelRegle[month] += montant;        // dont réglé
            }
          }
        });
      });

      return { companies, quotes, ttcMonthly, requestTypes, totalCompaniesYearly, totalQuotesYearly, signedQuotesYearly, totalRequestsYear, totalRequestsMonth, totalDevisAmountYear, totalDevisCountYear, totalDevisCountMonth, totalDevisSignedYear, totalDevisAmountMonth, caMensuel, caMensuelRegle, caMensuelGele };
    };

    const myCompanyStats = processData(companiesData);
    const foodInnovStats = processData(foodinnovCompaniesData);

    const calculateTotalTTC = (data: CompaniesData, month?: number) => {
      let totalTTC = 0;
      const currentYear = selectedYear;
  
      Object.values(data).forEach(company => {
        company.devis?.forEach(devis => {
          const devisDate = convertToDate(devis.updatedAt || devis.date);
          if (devisDate && devisDate.getFullYear() === currentYear) {
            if (month === undefined || devisDate.getMonth() === month) {
              if (devis.ttc) {
                totalTTC += devis.ttc;
              }
            }
          }
        });
      });
      return totalTTC;
    };

    return {
      myCompanies: myCompanyStats.companies,
      totalMyCompanies: myCompanyStats.totalCompaniesYearly,
      myCompanyQuotes: myCompanyStats.quotes,
      totalMyCompanyQuotes: myCompanyStats.totalQuotesYearly,
      signedMyCompanyQuotes: myCompanyStats.signedQuotesYearly,
      myCompanyTTCMonthly: myCompanyStats.ttcMonthly,
      requestTypes: myCompanyStats.requestTypes,
      foodInnovCompanies: foodInnovStats.companies,
      totalFoodInnovCompanies: foodInnovStats.totalCompaniesYearly,
      foodinnovQuotes: foodInnovStats.quotes,
      totalFoodInnovQuotes: foodInnovStats.totalQuotesYearly,
      signedFoodInnovQuotes: foodInnovStats.signedQuotesYearly,
      foodinnovTTCMonthly: foodInnovStats.ttcMonthly,
      foodinnovRequestTypes: foodInnovStats.requestTypes,
      myCompanyTTCYearly: calculateTotalTTC(companiesData),
      foodinnovTTCYearly: calculateTotalTTC(foodinnovCompaniesData),
      myCompanyTotalRequestsYear: myCompanyStats.totalRequestsYear,
      myCompanyTotalRequestsMonth: myCompanyStats.totalRequestsMonth,
      foodinnovTotalRequestsYear: foodInnovStats.totalRequestsYear,
      foodinnovTotalRequestsMonth: foodInnovStats.totalRequestsMonth,
      myCompanyDevisAmountYear: myCompanyStats.totalDevisAmountYear,
      myCompanyDevisCountYear: myCompanyStats.totalDevisCountYear,
      myCompanyDevisCountMonth: myCompanyStats.totalDevisCountMonth,
      myCompanyDevisSignedYear: myCompanyStats.totalDevisSignedYear,
      foodinnovDevisAmountYear: foodInnovStats.totalDevisAmountYear,
      foodinnovDevisCountYear: foodInnovStats.totalDevisCountYear,
      foodinnovDevisCountMonth: foodInnovStats.totalDevisCountMonth,
      foodinnovDevisSignedYear: foodInnovStats.totalDevisSignedYear,
      myCompanyDevisAmountMonth: myCompanyStats.totalDevisAmountMonth,
      foodinnovDevisAmountMonth: foodInnovStats.totalDevisAmountMonth,
      myCompanyCaMensuel: myCompanyStats.caMensuel,
      foodinnovCaMensuel: foodInnovStats.caMensuel,
      myCompanyCaMensuelRegle: myCompanyStats.caMensuelRegle,
      foodinnovCaMensuelRegle: foodInnovStats.caMensuelRegle,
      myCompanyCaMensuelGele: myCompanyStats.caMensuelGele,
      foodinnovCaMensuelGele: foodInnovStats.caMensuelGele,
    };
  };

  const getYearlyStats = () => {
    const processCompanies = (data: CompaniesData) => {
      const yearlyData = Array.from({ length: 12 }, (_, i) => ({
        month: new Date(0, i).toLocaleString('fr-FR', { month: 'short' }),
        companies: 0,
        requests: 0,
        devis: 0,
        ttc: 0,
      }));

      Object.values(data).forEach(company => {
        const createdAt = convertToDate(company.createdAt);
        if (createdAt && createdAt.getFullYear() === selectedYear) {
          const month = createdAt.getMonth();
          yearlyData[month].companies++;
        }

        company.opportunities?.forEach(opp => {
          const oppDate = convertToDate(opp.date);
          if (oppDate && oppDate.getFullYear() === selectedYear) {
            const month = oppDate.getMonth();
            yearlyData[month].requests++;
          }
        });

        company.devis?.forEach(devis => {
          const devisDate = convertToDate(devis.updatedAt || devis.date);
          if (devisDate && devisDate.getFullYear() === selectedYear) {
            const month = devisDate.getMonth();
            yearlyData[month].devis++;
            if(devis.ttc) {
                yearlyData[month].ttc += devis.ttc;
            }
          }
        });
      });
      return yearlyData;
    };

    return {
      myCompanies: processCompanies(companiesData),
      foodinnov: processCompanies(foodinnovCompaniesData),
    };
  };

  const availableYears = getAvailableYears();
  const stats = getCurrentMonthStats();
  const yearlyStats = getYearlyStats();

  const requestTypesData = stats?.requestTypes ? Array.from(stats.requestTypes.entries()).map(([name, value]) => ({ name, value })) : [];
  const foodinnovRequestTypesData = stats?.foodinnovRequestTypes ? Array.from(stats.foodinnovRequestTypes.entries()).map(([name, value]) => ({ name, value })) : [];

  // Ajout : Préparation des données CA Foodinnov/RegExport et marge par mois
  const getMonthlyCAAndMargin = () => {
    const months = Array.from({ length: 12 }, (_, i) => new Date(0, i).toLocaleString('fr-FR', { month: 'short' }));
    const caFoodinnov = Array(12).fill(0);          // facturé (hors gelées)
    const caRegExport = Array(12).fill(0);          // facturé (hors gelées)
    const caFoodinnovRegle = Array(12).fill(0);     // réglé uniquement
    const caRegExportRegle = Array(12).fill(0);     // réglé uniquement
    const caFoodinnovGele = Array(12).fill(0);      // ⚠️ gelé (suivi à part)
    const caRegExportGele = Array(12).fill(0);      // ⚠️ gelé (suivi à part)
    const caFoodinnovTTC = Array(12).fill(0);       // facturé TTC
    const caRegExportTTC = Array(12).fill(0);       // versé RegExport TTC

    // Factures FOODINNOV et RegExport (part amineAmount)
    Object.values(foodinnovCompaniesData).forEach(company => {
      company.invoices?.forEach((invoice: any) => {
        const invoiceDate = convertToDate(invoice.date);
        if (invoiceDate && invoiceDate.getFullYear() === selectedYear) {
          const month = invoiceDate.getMonth();
          const status = invoice.status || 'En attente de Paiement';
          const hasHT = typeof invoice.montantHT === 'number';
          const hasAmine = typeof invoice.amineAmount === 'number';
          if (hasHT) {
            caFoodinnov[month] += invoice.montantHT;                              // facturé = tout (incl. gelé)
            if (status === 'Gelée') caFoodinnovGele[month] += invoice.montantHT;   // dont gelé
            else if (status === 'Réglée') caFoodinnovRegle[month] += invoice.montantHT; // dont réglé
          }
          if (hasAmine) {
            caRegExport[month] += invoice.amineAmount;
            if (status === 'Gelée') caRegExportGele[month] += invoice.amineAmount;
            else if (status === 'Réglée') caRegExportRegle[month] += invoice.amineAmount;
          }
          if (typeof invoice.montantTTC === 'number') caFoodinnovTTC[month] += invoice.montantTTC;
          if (typeof invoice.amineAmountTTC === 'number') caRegExportTTC[month] += invoice.amineAmountTTC;
        }
      });
    });

    // 💰 Calcul de la marge
    const margeFoodinnov = caFoodinnov.map((ca, i) => ca - caRegExport[i]);
    const margeFoodinnovRegle = caFoodinnovRegle.map((ca, i) => ca - caRegExportRegle[i]);
    const margeFoodinnovGele = caFoodinnovGele.map((ca, i) => ca - caRegExportGele[i]);
    const margeFoodinnovTTC = caFoodinnovTTC.map((ca, i) => ca - caRegExportTTC[i]);

    // 📊 Format pour le BarChart
    return months.map((month, i) => ({
      month,
      caFoodinnov: caFoodinnov[i],
      caRegExport: caRegExport[i],
      margeFoodinnov: margeFoodinnov[i],
      caFoodinnovRegle: caFoodinnovRegle[i],
      caRegExportRegle: caRegExportRegle[i],
      margeFoodinnovRegle: margeFoodinnovRegle[i],
      caFoodinnovGele: caFoodinnovGele[i],
      caRegExportGele: caRegExportGele[i],
      margeFoodinnovGele: margeFoodinnovGele[i],
      caFoodinnovTTC: caFoodinnovTTC[i],
      caRegExportTTC: caRegExportTTC[i],
      margeFoodinnovTTC: margeFoodinnovTTC[i],
    }));
  };
  

  // Ajout : récupération des données pour le graphique CA/marge
  const caMargeData = getMonthlyCAAndMargin();
  const alertRegExportJune = caMargeData[5].caRegExport < 6480;

  // ⚠️ Impayés FOODINNOV = factures RegExport → FOODINNOV au statut "Gelée" (companiesData), par mois + total (HT & TTC)
  const foodinnovImpayesGele = Array(12).fill(0);
  const foodinnovImpayesGeleTTC = Array(12).fill(0);
  Object.entries(companiesData).forEach(([companyId, company]: [string, any]) => {
    if (!companyId.toUpperCase().includes('FOODINNOV')) return;
    company.invoices?.forEach((invoice: any) => {
      const d = convertToDate(invoice.date);
      if (d && d.getFullYear() === selectedYear && (invoice.status || 'En attente de Paiement') === 'Gelée') {
        foodinnovImpayesGele[d.getMonth()] += invoiceFactureHT(invoice);
        foodinnovImpayesGeleTTC[d.getMonth()] += Number(invoice.montantFactureTTC ?? invoice.montantTTC) || 0;
      }
    });
  });
  const foodinnovImpayesGeleTotal = foodinnovImpayesGele.reduce((a, b) => a + b, 0);
  const foodinnovImpayesGeleTotalTTC = foodinnovImpayesGeleTTC.reduce((a, b) => a + b, 0);
  // Données du graphique FoodInnov enrichies de l'impayé (gelé) FOODINNOV
  const caMargeDataChart = caMargeData.map((d, i) => ({ ...d, impayeFoodinnov: foodinnovImpayesGele[i] }));

  // Section FoodInnov : le contrat a démarré en juin 2025 → pour 2025 on part de juin, sinon de janvier
  const foodinnovStartMonth = selectedYear === 2025 ? 5 : 0; // 5 = juin (0-indexé)
  const foodinnovEndMonth = selectedYear === new Date().getFullYear() ? new Date().getMonth() : 11;
  const foodinnovMonths: number[] = [];
  for (let m = foodinnovStartMonth; m <= foodinnovEndMonth; m++) foodinnovMonths.push(m);
  const caMargeDataChartFoodinnov = caMargeDataChart.filter((_, i) => i >= foodinnovStartMonth && i <= foodinnovEndMonth);

  // Fonction pour calculer la répartition du CA des factures par type de demande pour le mois en cours
  const getCARepartitionByRequestTypeMonth = () => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const typeMap = new Map<string, number>();

    Object.values(foodinnovCompaniesData).forEach(company => {
      company.invoices?.forEach((invoice: any) => {
        const invoiceDate = convertToDate(invoice.date);
        if (invoiceDate &&
            invoiceDate.getFullYear() === currentYear &&
            invoiceDate.getMonth() === currentMonth &&
            invoice.opportunityType &&
            (invoice.status || 'En attente de Paiement') !== 'Gelée' &&
            typeof invoice.montantHT === 'number') {
          const type = invoice.opportunityType;
          typeMap.set(type, (typeMap.get(type) || 0) + invoiceFactureHT(invoice));
        }
      });
    });

    return Array.from(typeMap.entries())
      .map(([name, value]) => ({ name, value }))
      .filter(item => item.value > 0);
  };

  // Fonction pour calculer la répartition du CA des factures par type de demande pour l'année sélectionnée
  const getCARepartitionByRequestTypeYear = () => {
    const typeMap = new Map<string, number>();

    Object.values(foodinnovCompaniesData).forEach(company => {
      company.invoices?.forEach((invoice: any) => {
        const invoiceDate = convertToDate(invoice.date);
        if (invoiceDate &&
            invoiceDate.getFullYear() === selectedYear &&
            invoice.opportunityType &&
            (invoice.status || 'En attente de Paiement') !== 'Gelée' &&
            typeof invoice.montantHT === 'number') {
          const type = invoice.opportunityType;
          typeMap.set(type, (typeMap.get(type) || 0) + invoiceFactureHT(invoice));
        }
      });
    });

    return Array.from(typeMap.entries())
      .map(([name, value]) => ({ name, value }))
      .filter(item => item.value > 0);
  };

  const caRepartitionByRequestTypeMonth = getCARepartitionByRequestTypeMonth();
  const caRepartitionByRequestTypeYear = getCARepartitionByRequestTypeYear();

  return (
    <div className="home-container">
      <ToastContainer position="top-right" autoClose={2000} />
      {loading && (
        <div className="global-loader" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(255,255,255,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', color: '#2E86C1' }}>
          Chargement...
        </div>
      )}
      <Header
        isAuthenticated={isAuthenticated}
        onLoginClick={handleLoginClick}
        onHomeClick={handleHomeClick}
        onLogoutClick={handleLogoutClick}
      />


      <main className="home-container" style={{ marginBottom: '4rem', minHeight: 'calc(100vh - 200px)', paddingBottom: '2rem' }}>
        {isAuthenticated ? (
          <div className="dashboard-container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
            {(userRole === 'admin' || userRole === 'demo') && (
              <div className="stats-section">
                <div className="section-header">
                  <img 
                    src="/RegExport_Conseil_RVB_SYMBOLE_1coul_Bleu.png" 
                    alt="RegExport Conseil" 
                    className="section-icon"
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <h2 style={{ color: '#424272', margin: 0 }}>Statistiques de l'année {selectedYear}</h2>
                    {availableYears.length > 0 && (
                      <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        style={{
                          padding: '0.5rem 1rem',
                          fontSize: '1rem',
                          border: '1px solid #424272',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          color: '#424272',
                          cursor: 'pointer'
                        }}
                      >
                        {availableYears.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
                <table className="stats-table">
                  <thead>
                    <tr>
                      <th>Types de demandes</th>
                      <th>Devis enregistrés</th>
                      <th>Montant Total (HT)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td data-label="Types de demandes">
                        <div className="mobile-section-title">Types de demandes</div>
                        <div className="stat-item">
                          <span>Demandes de l'année {selectedYear}</span>{' '}
                          <strong>{Array.from(stats?.requestTypes?.values() || []).reduce((a, b) => a + b, 0)}</strong>
                        </div>
                        {selectedYear === new Date().getFullYear() && (
                          <div className="stat-item">
                            <span>Demandes du mois en cours</span>{' '}
                            <strong>{stats?.myCompanyQuotes || 0}</strong>
                          </div>
                        )}
                        {stats?.requestTypes && Array.from(stats.requestTypes.entries()).map(([type, count]) => (
                          <div key={type} className="stat-item">
                            <span>{type}</span>{' '}
                            <strong>{count}</strong>
                          </div>
                        ))}
                      </td>
                      <td data-label="Devis enregistrés">
                        <div className="mobile-section-title">Devis enregistrés</div>
                        <div className="stat-item">
                          <span>Montant des devis de l'année</span>{' '}
                          <strong>{stats?.myCompanyDevisAmountYear?.toFixed(2)}€</strong>
                        </div>
                        {selectedYear === new Date().getFullYear() && (
                          <div className="stat-item">
                            <span>Montant des devis du mois</span>{' '}
                            <strong>{stats?.myCompanyDevisAmountMonth?.toFixed(2)}€</strong>
                          </div>
                        )}
                        <div className="stat-item">
                          <span>Devis de l'année {selectedYear}</span>{' '}
                          <strong>{stats?.myCompanyDevisCountYear || 0}</strong>
                        </div>
                        {selectedYear === new Date().getFullYear() && (
                          <div className="stat-item">
                            <span>Devis du mois</span>{' '}
                            <strong>{stats?.myCompanyDevisCountMonth || 0}</strong>
                          </div>
                        )}
                        <div className="stat-item">
                          <span>Devis signés</span>{' '}
                          <strong>{stats?.myCompanyDevisSignedYear || 0}</strong>
                        </div>
                      </td>
                      <td data-label="Montant Total (HT)">
                        <div className="mobile-section-title">Montant Total (HT)</div>
                        <div className="stat-item">
                          <span>CA facturé {selectedYear}:</span>{' '}
                          <strong>{(stats?.myCompanyCaMensuel?.reduce((a, b) => a + b, 0) || 0).toFixed(2)}€</strong>
                        </div>
                        <div className="stat-item">
                          <span>CA réglé {selectedYear}:</span>{' '}
                          <strong style={{ color: '#155724' }}>{(stats?.myCompanyCaMensuelRegle?.reduce((a, b) => a + b, 0) || 0).toFixed(2)}€</strong>
                        </div>
                        <div className="stat-item" style={{ backgroundColor: '#fff3cd', borderRadius: '4px', padding: '2px 6px' }}>
                          <span>⚠️ CA gelé {selectedYear}:</span>{' '}
                          <strong style={{ color: '#856404' }}>{(stats?.myCompanyCaMensuelGele?.reduce((a, b) => a + b, 0) || 0).toFixed(2)}€</strong>
                        </div>
                        {selectedYear === new Date().getFullYear() && (
                          <div className="stat-item">
                            <span>CA facturé du mois:</span>{' '}
                            <strong>{(stats?.myCompanyCaMensuel?.[new Date().getMonth()] || 0).toFixed(2)}€</strong>
                          </div>
                        )}
                        {selectedYear === new Date().getFullYear() && (
                          <div className="stat-item">
                            <span>CA réglé du mois:</span>{' '}
                            <strong style={{ color: '#155724' }}>{(stats?.myCompanyCaMensuelRegle?.[new Date().getMonth()] || 0).toFixed(2)}€</strong>
                          </div>
                        )}
                        {/* CA mensuel par mois de l'année sélectionnée (invoices) : facturé / réglé */}
                        {Array.from({length: selectedYear === new Date().getFullYear() ? new Date().getMonth() + 1 : 12}, (_, i) => (
                          <div key={i} className="stat-item">
                            <span>CA {new Date(0, i).toLocaleString('fr-FR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase())} :</span>{' '}
                            <strong>{stats?.myCompanyCaMensuel?.[i]?.toFixed(2) || '0.00'}€</strong>
                            {' '}<span style={{ color: '#155724' }}>(réglé : {stats?.myCompanyCaMensuelRegle?.[i]?.toFixed(2) || '0.00'}€)</span>
                            {(stats?.myCompanyCaMensuelGele?.[i] || 0) > 0 && (
                              <span style={{ color: '#856404' }}>{' '}⚠️ (gelé : {stats?.myCompanyCaMensuelGele?.[i]?.toFixed(2)}€)</span>
                            )}
                          </div>
                        ))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {(userRole === 'admin' || userRole === 'demo') && (
              <div className="stats-section">
                <div className="section-header">
                  <img 
                    src="/RegExport_Conseil_RVB_SYMBOLE_1coul_Bleu.png" 
                    alt="RegExport Conseil" 
                    className="section-icon"
                  />
                  <h2 style={{ color: '#424272' }}>Graphiques</h2>
                </div>
                <div className="charts-container" style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <div className="chart" style={{ flex: 1, height: '250px' }}>
                    <div className="mobile-section-title">Nombre de sociétés par mois</div>
                    <h3 className="desktop-section-title" style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Nombre de sociétés par mois</h3>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                        <Pie
                          data={yearlyStats.myCompanies}
                          dataKey="companies"
                          nameKey="month"
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={80}
                          label={renderCustomizedLabel}
                        >
                          {yearlyStats.myCompanies.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value: number) => [`${value} sociétés`, 'Nombre']}
                          contentStyle={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: 'none',
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        />
                        <Legend 
                          layout="horizontal" 
                          verticalAlign="bottom" 
                          align="center"
                          wrapperStyle={{
                            paddingTop: '20px'
                          }}
                          formatter={(value) => (
                            <span style={{ fontSize: '10px' }}>{value}</span>
                          )}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="chart" style={{ flex: 1, height: '250px' }}>
                    <div className="mobile-section-title">Nombre de demandes par mois</div>
                    <h3 className="desktop-section-title" style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Nombre de demandes par mois</h3>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={yearlyStats.myCompanies}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="caRegExport" name="CA RegExport">
  {caMargeData.map((entry, index) => (
    <Cell
      key={`cell-regexport-${index}`}
      fill={index === 5 ? "#C0392B" : "#2E86C1"} // rouge pour juin
    />
  ))}
</Bar>
                        </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="chart" style={{ flex: 1, height: '250px' }}>
                    <div className="mobile-section-title">Répartition % des types de demandes (mois)</div>
                    <h3 className="desktop-section-title" style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Répartition % des types de demandes (mois)</h3>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                        <Pie
                          data={requestTypesData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={80}
                          label={renderCustomizedLabel}
                        >
                          {requestTypesData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [value, name]}
                          contentStyle={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: 'none',
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        />
                        <Legend
                          layout="horizontal"
                          verticalAlign="bottom"
                          align="center"
                          wrapperStyle={{
                            paddingTop: '20px'
                          }}
                          formatter={(value) => (
                            <span style={{ fontSize: '10px' }}>{value}</span>
                          )}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {(userRole === 'admin' || userRole === 'foodinnov') && (
              <div className="stats-section">
                <div className="section-header">
                  <img 
                    src="/RegExport_Conseil_RVB_SYMBOLE_1coul_Bleu.png" 
                    alt="RegExport Conseil" 
                    className="section-icon"
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <h2 style={{ color: '#424272', margin: 0 }}>Statistiques FoodInnov de l'année {selectedYear}</h2>
                    {availableYears.length > 0 && (
                      <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        style={{
                          padding: '0.5rem 1rem',
                          fontSize: '1rem',
                          border: '1px solid #424272',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          color: '#424272',
                          cursor: 'pointer'
                        }}
                      >
                        {availableYears.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
                {/* Ajout d'un message d'erreur si pas de données */}
                {Object.keys(foodinnovCompaniesData).length === 0 && (
                  <div style={{ color: 'red', marginBottom: '1rem' }}>
                    Aucune donnée FoodInnov disponible.
                  </div>
                )}
                <table className="stats-table">
                  <thead>
                    <tr>
                      <th>Types de demandes</th>
                      <th>Devis enregistrés</th>
                      <th>MONTANT TOTAL HT<br />FOODINNOV</th>
                      <th>MONTANT TOTAL HT<br />REGEXPORT</th>
                      <th>MARGE</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td data-label="Types de demandes">
                        <div className="mobile-section-title">Types de demandes</div>
                        <div className="stat-item">
                          <span>Demandes de l'année {selectedYear}</span>{' '}
                          <strong>{Array.from(stats?.foodinnovRequestTypes?.values() || []).reduce((a, b) => a + b, 0)}</strong>
                        </div>
                        {selectedYear === new Date().getFullYear() && (
                          <div className="stat-item">
                            <span>Demandes du mois en cours</span>{' '}
                            <strong>{stats?.foodinnovQuotes || 0}</strong>
                          </div>
                        )}
                        {stats?.foodinnovRequestTypes && Array.from(stats.foodinnovRequestTypes.entries()).map(([type, count]) => (
                          <div key={type} className="stat-item">
                            <span>{type}</span> {' '}
                            <strong>{count}</strong>
                          </div>
                        ))}
                      </td>
                      <td data-label="Devis enregistrés">
                        <div className="mobile-section-title">Devis enregistrés</div>
                        <div className="stat-item">
                          <span>Montant des devis de l'année</span>{' '}
                          <strong>{stats?.foodinnovDevisAmountYear?.toFixed(2)}€</strong>
                        </div>
                        {selectedYear === new Date().getFullYear() && (
                          <div className="stat-item">
                            <span>Montant des devis du mois</span>{' '}
                            <strong>{stats?.foodinnovDevisAmountMonth?.toFixed(2)}€</strong>
                          </div>
                        )}
                        <div className="stat-item">
                          <span>Devis de l'année {selectedYear}</span>{' '}
                          <strong>{stats?.foodinnovDevisCountYear || 0}</strong>
                        </div>
                        {selectedYear === new Date().getFullYear() && (
                          <div className="stat-item">
                            <span>Devis du mois</span>{' '}
                            <strong>{stats?.foodinnovDevisCountMonth || 0}</strong>
                          </div>
                        )}
                        <div className="stat-item">
                          <span>Devis signés</span>{' '}
                          <strong>{stats?.foodinnovDevisSignedYear || 0}</strong>
                        </div>
                      </td>
                      <td data-label="MONTANT TOTAL HT FOODINNOV">
                        <div className="mobile-section-title">MONTANT TOTAL HT FOODINNOV</div>
                        <div className="stat-item">
                          <span>CA facturé {selectedYear}:</span>{' '}
                          <strong>{(stats?.foodinnovCaMensuel?.reduce((a, b) => a + b, 0) || 0).toFixed(2)}€</strong>
                        </div>
                        <div className="stat-item">
                          <span>CA réglé {selectedYear}:</span>{' '}
                          <strong style={{ color: '#155724' }}>{(stats?.foodinnovCaMensuelRegle?.reduce((a, b) => a + b, 0) || 0).toFixed(2)}€</strong>
                        </div>
                        {/* CA mensuel (section FoodInnov : démarre en juin pour 2025) : facturé / réglé */}
                        {foodinnovMonths.map((i) => {
                          const monthName = new Date(0, i).toLocaleString('fr-FR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase());
                          return (
                            <div key={i} className="stat-item">
                              <span>CA {monthName} :</span>{' '}
                              <strong>{(stats?.foodinnovCaMensuel?.[i] || 0).toFixed(2)}€</strong>
                              {' '}<span style={{ color: '#155724' }}>(réglé : {(stats?.foodinnovCaMensuelRegle?.[i] || 0).toFixed(2)}€)</span>
                            </div>
                          );
                        })}
                      </td>
                      <td data-label="MONTANT TOTAL HT REGEXPORT">
                        <div className="mobile-section-title">MONTANT TOTAL HT REGEXPORT</div>
                        <div className="stat-item">
                          <span>CA facturé {selectedYear}:</span>{' '}
                          <strong>{(caMargeData.reduce((sum, month) => sum + month.caRegExport, 0)).toFixed(2)}€</strong>
                        </div>
                        <div className="stat-item">
                          <span>CA réglé {selectedYear}:</span>{' '}
                          <strong style={{ color: '#155724' }}>{(caMargeData.reduce((sum, month) => sum + month.caRegExportRegle, 0)).toFixed(2)}€</strong>
                        </div>
                        {foodinnovMonths.map((i) => {
                          const monthName = new Date(0, i).toLocaleString('fr-FR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase());
                          return (
                            <div key={i} className="stat-item">
                              <span>CA {monthName} :</span>{' '}
                              <strong>{(caMargeData[i]?.caRegExport || 0).toFixed(2)}€</strong>
                              {' '}<span style={{ color: '#155724' }}>(réglé : {(caMargeData[i]?.caRegExportRegle || 0).toFixed(2)}€)</span>
                            </div>
                          );
                        })}
                      </td>
                      <td data-label="MARGE">
                        <div className="mobile-section-title">MARGE</div>
                        <div className="stat-item">
                          <span>Marge {selectedYear}:</span>{' '}
                          <strong>{(caMargeData.reduce((sum, month) => sum + month.margeFoodinnov, 0)).toFixed(2)}€</strong>
                        </div>
                        {foodinnovMonths.map((i) => {
                          const monthName = new Date(0, i).toLocaleString('fr-FR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase());
                          return (
                            <div key={i} className="stat-item">
                              <span>Marge {monthName} :</span>{' '}
                              <strong>{(caMargeData[i]?.margeFoodinnov || 0).toFixed(2)}€</strong>
                            </div>
                          );
                        })}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* ⚠️ Ce que FOODINNOV n'a pas réglé (factures gelées) — par mois + total */}
                <div style={{ marginTop: '16px', padding: '14px 18px', backgroundColor: '#fff3cd', border: '1px solid #ffeeba', borderRadius: '6px' }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#856404', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1rem' }}>
                    ⚠️ Factures FOODINNOV gelées (non réglées) — {selectedYear}
                  </h4>
                  {foodinnovImpayesGeleTotal > 0 ? (
                    <>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', color: '#856404', fontSize: '0.95rem' }}>
                        {foodinnovImpayesGele.map((val, i) => val > 0 ? (
                          <span key={i}>
                            <strong>{new Date(0, i).toLocaleString('fr-FR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase())} :</strong> {val.toFixed(2)}€
                          </span>
                        ) : null)}
                      </div>
                      <div style={{ marginTop: '10px', fontWeight: 700, color: '#856404', fontSize: '1.05rem' }}>
                        Total non réglé (gelé) : {foodinnovImpayesGeleTotal.toFixed(2)}€
                      </div>
                    </>
                  ) : (
                    <div style={{ color: '#856404' }}>Aucune facture gelée pour {selectedYear}.</div>
                  )}
                </div>
              </div>
            )}

            {(userRole === 'admin' || userRole === 'foodinnov') && (
              <div className="stats-section">
                <div className="section-header">
                  <img
                    src="/RegExport_Conseil_RVB_SYMBOLE_1coul_Bleu.png"
                    alt="RegExport Conseil"
                    className="section-icon"
                  />
                  <h2 style={{ color: '#424272' }}>Graphiques FoodInnov</h2>
                </div>
                <div className="charts-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'stretch', gap: '1.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                  {/* Bloc récapitulatif de l'année */}
                  <div className="chart" style={{ width: '50%', minWidth: '320px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                    <h3 className="desktop-section-title" style={{
                      fontSize: '1.2rem',
                      marginBottom: '1rem',
                      fontWeight: 600,
                      color: '#424272',
                      textAlign: 'center',
                      paddingBottom: '0.5rem',
                      borderBottom: '2px solid #2E86C1'
                    }}>Récapitulatif {selectedYear}</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderRadius: '8px', background: '#eafaf1', borderLeft: '5px solid #27AE60' }}>
                        <span style={{ fontWeight: 600, color: '#1e7e4f' }}>Total facturé</span>
                        <strong style={{ fontSize: '1.4rem', color: '#27AE60' }}>
                          {caMargeData.reduce((s, m) => s + m.caFoodinnov, 0).toFixed(2)} € HT
                          <span style={{ fontSize: '0.85rem', fontWeight: 500 }}> ({caMargeData.reduce((s, m) => s + m.caFoodinnovTTC, 0).toFixed(2)} € TTC)</span>
                        </strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderRadius: '8px', background: '#eaf2fb', borderLeft: '5px solid #2E86C1' }}>
                        <span style={{ fontWeight: 600, color: '#1f5c8b' }}>Total versé à REGEXPORT CONSEIL</span>
                        <strong style={{ fontSize: '1.4rem', color: '#2E86C1' }}>
                          {caMargeData.reduce((s, m) => s + m.caRegExport, 0).toFixed(2)} € HT
                          <span style={{ fontSize: '0.85rem', fontWeight: 500 }}> ({caMargeData.reduce((s, m) => s + m.caRegExportTTC, 0).toFixed(2)} € TTC)</span>
                        </strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderRadius: '8px', background: '#fdf1e7', borderLeft: '5px solid #E67E22' }}>
                        <span style={{ fontWeight: 600, color: '#a85618' }}>Total Marge</span>
                        <strong style={{ fontSize: '1.4rem', color: '#E67E22' }}>
                          {caMargeData.reduce((s, m) => s + m.margeFoodinnov, 0).toFixed(2)} € HT
                          <span style={{ fontSize: '0.85rem', fontWeight: 500 }}> ({caMargeData.reduce((s, m) => s + m.margeFoodinnovTTC, 0).toFixed(2)} € TTC)</span>
                        </strong>
                      </div>
                    </div>
                  </div>

                  {/* Bloc manque à gagner : montant dû à RegExport non versé (factures gelées) */}
                  <div className="chart" style={{
                    width: '38%',
                    minWidth: '300px',
                    border: '2px solid #dc3545',
                    borderRadius: '10px',
                    background: 'linear-gradient(180deg, #fff5f5 0%, #fff 100%)',
                    padding: '1.25rem 1.5rem',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '2rem', lineHeight: 1 }}>⚠️</span>
                      <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#b02a37' }}>
                        Manque à gagner RegExport&nbsp;— non versé {selectedYear}
                      </h3>
                    </div>
                    <div style={{
                      fontWeight: 800,
                      color: '#dc3545',
                      textAlign: 'center',
                      margin: '0.5rem 0 1rem',
                      letterSpacing: '0.5px'
                    }}>
                      <div style={{ fontSize: '2.1rem' }}>{foodinnovImpayesGeleTotal.toFixed(2)} € HT</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>({foodinnovImpayesGeleTotalTTC.toFixed(2)} € TTC)</div>
                    </div>
                    {foodinnovImpayesGeleTotal > 0 ? (
                      <>
                        <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#b02a37', marginBottom: '6px' }}>
                          Mois avec factures gelées :
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', overflowY: 'auto' }}>
                          {foodinnovImpayesGele.map((val, i) => val > 0 ? (
                            <div key={i} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '6px 10px',
                              background: '#f8d7da',
                              borderRadius: '6px',
                              color: '#721c24',
                              fontSize: '0.95rem'
                            }}>
                              <span>{new Date(0, i).toLocaleString('fr-FR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase())}</span>
                              <strong>{val.toFixed(2)} € HT <span style={{ fontWeight: 500 }}>({foodinnovImpayesGeleTTC[i].toFixed(2)} € TTC)</span></strong>
                            </div>
                          ) : null)}
                        </div>
                      </>
                    ) : (
                      <div style={{ color: '#155724', textAlign: 'center', fontWeight: 600 }}>
                        ✅ Aucune facture gelée en {selectedYear}
                      </div>
                    )}
                  </div>
                </div>

                {/* 2ᵉ ligne : graphique d'évolution du CA par mois */}
                <div className="charts-container" style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
                  <div className="chart" style={{ width: '80%', minWidth: '320px', height: '340px' }}>
                    <div className="mobile-section-title">Évolution du CA par mois</div>
                    <h3 className="desktop-section-title" style={{
                      fontSize: '1.2rem',
                      marginBottom: '1rem',
                      fontWeight: 600,
                      color: '#424272',
                      textAlign: 'center',
                      paddingBottom: '0.5rem',
                      borderBottom: '2px solid #2E86C1'
                    }}>Évolution du CA par mois</h3>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={caMargeDataChartFoodinnov}
                        margin={{ left: 0, right: 10, top: 20, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 10, textAnchor: 'start' }} />
                        <YAxis tick={{ fontSize: 10, textAnchor: 'start' }} />
                        <Tooltip formatter={(value: number) => `${value.toFixed(2)} €`} />
                        <Legend />
                        <Bar dataKey="caFoodinnov" fill="#27AE60" name="CA FOODINNOV" />
                        <Bar dataKey="caRegExport" fill="#2E86C1" name="CA RegExport" />
                        <Bar dataKey="margeFoodinnov" fill="#E67E22" name="Marge FOODINNOV" />
                        <Bar dataKey="impayeFoodinnov" fill="#E9A100" name="⚠️ Impayé FOODINNOV (gelé)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div style={{ borderTop: '2px solid #e9ecef', marginTop: '2rem', paddingTop: '2rem' }}>
                  <div className="charts-container" style={{ display: 'flex', gap: '1rem' }}>
                  <div className="chart" style={{ flex: 1, height: '250px' }}>
                    <div className="mobile-section-title">Répartition du CA des factures par type de demande ({new Date().toLocaleString('fr-FR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase())})</div>
                    <h3 className="desktop-section-title" style={{ 
                      fontSize: '1.1rem', 
                      marginBottom: '1rem',
                      fontWeight: 600,
                      color: '#424272',
                      textAlign: 'center',
                      paddingBottom: '0.5rem',
                      borderBottom: '2px solid #27AE60'
                    }}>
                      Répartition du CA des factures par type de demande ({new Date().toLocaleString('fr-FR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase())})
                    </h3>
                    {selectedYear === new Date().getFullYear() && caRepartitionByRequestTypeMonth.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                          <Pie
                            data={caRepartitionByRequestTypeMonth}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={80}
                            label={renderExternalLabel}
                            labelLine={true}
                          >
                            {caRepartitionByRequestTypeMonth.map((entry, index) => (
                              <Cell key={`cell-ca-month-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value: number) => `${value.toFixed(2)} €`}
                            contentStyle={{
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: 'none',
                              borderRadius: '4px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#666' }}>
                        {selectedYear !== new Date().getFullYear() 
                          ? `Sélectionnez l'année ${new Date().getFullYear()} pour voir les données du mois`
                          : 'Aucune donnée pour le mois en cours'}
                      </div>
                    )}
                  </div>
                  <div className="chart" style={{ flex: 1, height: '250px' }}>
                    <div className="mobile-section-title">Répartition du CA des factures par type de demande (année)</div>
                    <h3 className="desktop-section-title" style={{ 
                      fontSize: '1.1rem', 
                      marginBottom: '1rem',
                      fontWeight: 600,
                      color: '#424272',
                      textAlign: 'center',
                      paddingBottom: '0.5rem',
                      borderBottom: '2px solid #E67E22'
                    }}>
                      Répartition du CA des factures par type de demande (année {selectedYear})
                    </h3>
                    {caRepartitionByRequestTypeYear.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                          <Pie
                            data={caRepartitionByRequestTypeYear}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={80}
                            label={renderExternalLabel}
                            labelLine={true}
                          >
                            {caRepartitionByRequestTypeYear.map((entry, index) => (
                              <Cell key={`cell-ca-year-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value: number) => `${value.toFixed(2)} €`}
                            contentStyle={{
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: 'none',
                              borderRadius: '4px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#666' }}>
                        Aucune donnée pour l'année {selectedYear}
                      </div>
                    )}
                  </div>
                </div>
                </div>
              </div>
            )}
          </div>
        ) : showLogin ? (
          <div style={{ opacity: actionLoading ? 0.5 : 1, pointerEvents: actionLoading ? 'none' : 'auto' }}>
            <LoginForm onLoginSuccess={handleLoginSuccess} />
            {actionLoading && <div style={{ textAlign: 'center', marginTop: '1rem', color: '#2E86C1' }}>Connexion en cours...</div>}
          </div>
        ) : (
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold mb-4">Bienvenue sur NormExport</h1>
            <p className="text-lg">Connectez-vous pour accéder à votre espace.</p>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Home;
